import { Injectable, signal, inject, PLATFORM_ID, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { SupabaseService } from './supabase.service';
import { User as AppUser, UserPlan } from '../models/user.model';
import { LogService } from './log.service';
import { environment } from '../environments/environment.generated';

export type NotificationType = 'success' | 'error' | 'info';

export interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
}

const PENDING_PRICE_ID_KEY = 'correctify-pending-price-id';
const AUTH_OPERATION_TIMEOUT_MS = 30000;
const PROFILE_LOAD_TIMEOUT_MS = 20000;
const LGPD_LEGAL_VERSION = '2026-03-30';
const PENDING_LGPD_CONSENT_KEY = 'correctify-pending-lgpd-consent';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private supabaseService = inject(SupabaseService);
  private supabase = this.supabaseService.client;
  private logService = inject(LogService);
  private platformId = inject(PLATFORM_ID);

  readonly session = signal<Session | null>(null);
  readonly currentUser = signal<AppUser | null>(null);
  readonly isAuthenticated = signal<boolean>(false);
  readonly isAdmin = signal<boolean>(false);
  readonly pendingPriceId = signal<string | null>(this.getInitialPendingPriceId());
  readonly effectiveCredits = computed(() => Math.max(0, Number(this.currentUser()?.credits ?? 0)));

  readonly notifications = signal<Notification[]>([]);
  private nextId = 0;
  private profileLoadInFlight: Promise<void> | null = null;

  constructor() {
    this.supabase.auth.getSession().then(async ({ data, error }) => {
      if (error) {
        this.showNotification('error', 'Erro de autenticação', `Erro ao carregar sessão: ${error.message}`);
        // Avoid clearing here to prevent race with OAuth callback session hydration.
        return;
      }

      if (data.session) {
        try {
          await this.setSession(data.session);
        } catch {
        }
      } else {
        // Initial state is already signed-out; keep as no-op to avoid auth race conditions.
      }
    }).catch(() => {
      this.showNotification('error', 'Erro crítico', 'Não foi possível conectar ao serviço de autenticação.');
      // Keep current auth state; avoid forcing clear during transient startup failures.
    });

    this.supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        if (event === 'SIGNED_IN') {
          this.logService.log('INFO', 'User signed in');
        }
        if (event === 'TOKEN_REFRESHED' && this.currentUser()) {
          this.session.set(session);
          this.isAuthenticated.set(true);
          return;
        }
        try {
          await this.setSession(session);
          if (event === 'SIGNED_IN') {
            await this.applyPendingLgpdConsentIfAny().catch(() => undefined);
          }
        } catch {
        }
      } else {
        if (event === 'SIGNED_OUT') {
          this.logService.log('INFO', 'User signed out');
        }
        this.clearSession();
      }
    });
  }

  private getInitialPendingPriceId(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return sessionStorage.getItem(PENDING_PRICE_ID_KEY);
    }
    return null;
  }

  showNotification(type: NotificationType, title: string, message: string, duration = 5000): void {
    const id = this.nextId++;
    const newNotification: Notification = { id, type, title, message };
    this.notifications.update((current) => [...current, newNotification]);

    if (duration > 0) {
      setTimeout(() => this.removeNotification(id), duration);
    }
  }

  removeNotification(id: number): void {
    this.notifications.update((current) => current.filter((n) => n.id !== id));
  }

  private clearSession(): void {
    this.session.set(null);
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.isAdmin.set(false);
    this.profileLoadInFlight = null;
  }

  private clearPersistedAuthStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const storageKey = (this.supabase.auth as any)?.storageKey as string | undefined;
    if (!storageKey) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}-user`);
      localStorage.removeItem(`${storageKey}-code-verifier`);
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(`${storageKey}-user`);
      sessionStorage.removeItem(`${storageKey}-code-verifier`);
    } catch {
      // no-op
    }
  }

  private handleSessionExpired(): void {
    this.clearSession();
    this.clearPersistedAuthStorage();
    void this.supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
  }

  private withTimeout<T>(promise: Promise<T>, message: string, timeoutMs = AUTH_OPERATION_TIMEOUT_MS): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error(message)), timeoutMs);
      promise
        .then((value) => {
          clearTimeout(timeout);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  private async setSession(session: Session): Promise<void> {
    this.session.set(session);
    try {
      await this.refreshUserProfile();
    } catch (error: any) {
      const message = error?.message ?? 'Não foi possível carregar seu perfil.';
      const isTimeout = message.toLowerCase().includes('tempo limite');
      if (!isTimeout && this.currentUser()) {
        this.showNotification('error', 'Erro de perfil', message);
      }
      const isAuthError =
        message.toLowerCase().includes('jwt')
        || message.toLowerCase().includes('token')
        || message.toLowerCase().includes('unauthorized')
        || message.toLowerCase().includes('401');

      if (isAuthError) {
        this.handleSessionExpired();
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      if (!this.currentUser()) {
        const fallbackUser = this.buildFallbackUser(session);
        this.currentUser.set(fallbackUser);
        this.isAdmin.set(false);
        this.isAuthenticated.set(true);

        setTimeout(() => {
          void this.refreshUserProfile().catch(() => undefined);
        }, 1200);
      }
    }
  }

  private isAdminMfaRequired(): boolean {
    const raw = String(
      (environment as any).REQUIRE_ADMIN_MFA
      ?? (environment as any).ADMIN_REQUIRE_MFA
      ?? ''
    ).trim().toLowerCase();

    return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
  }

  async ensureAdminPrivilegedSession(actionLabel = 'esta operacao administrativa'): Promise<void> {
    const user = this.currentUser();
    if (!user?.isAdmin) {
      throw new Error('Acesso restrito ao administrador.');
    }

    const { data: sessionData, error: sessionError } = await this.supabase.auth.getSession();
    if (sessionError || !sessionData.session?.access_token) {
      this.handleSessionExpired();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    this.session.set(sessionData.session);

    if (!this.isAdminMfaRequired()) {
      return;
    }

    const mfaApi = (this.supabase.auth as any)?.mfa;
    if (!mfaApi?.getAuthenticatorAssuranceLevel) {
      throw new Error(`MFA obrigatória para ${actionLabel}. Configure um fator antes de continuar.`);
    }

    const { data: assuranceData, error: assuranceError } = await mfaApi.getAuthenticatorAssuranceLevel();
    if (assuranceError) {
      throw new Error(`Não foi possível validar MFA para ${actionLabel}.`);
    }

    const currentLevel = String(
      assuranceData?.currentLevel
      ?? assuranceData?.current_level
      ?? 'aal1'
    ).toLowerCase();

    if (currentLevel !== 'aal2') {
      throw new Error(`Confirme sua autenticação multifator para ${actionLabel}.`);
    }
  }

  getDisplayCredits(user: AppUser | null): number {
    if (!user) {
      return 0;
    }

    return Math.max(0, Number(user.credits ?? 0));
  }

  async consumeFreeCredit(options: { useProTrial: boolean }): Promise<void> {
    const user = this.currentUser();
    if (!user) throw new Error('Usuário não encontrado.');

    const nowIso = new Date().toISOString();
    const updates: Record<string, unknown> = {
      last_free_correction_at: nowIso,
      credits: 0,
    };

    if (options.useProTrial) {
      updates['has_used_pro_trial'] = true;
    }

    const { error } = await this.supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw new Error(`Falha ao consumir crédito grátis: ${error.message}`);

    this.currentUser.update((current) => {
      if (!current) return null;

      const hasUsedProTrial = options.useProTrial ? true : current.has_used_pro_trial;
      return {
        ...current,
        has_used_pro_trial: hasUsedProTrial,
        last_free_correction_at: nowIso,
        credits: 0,
      };
    });
  }

  private buildFallbackUser(session: Session): AppUser {
    return {
      id: session.user.id,
      name: (session.user.user_metadata?.name as string | undefined) ?? session.user.email ?? 'Usuário',
      email: session.user.email ?? '',
      credits: 0,
      plan: 'gratuito',
      essaysSubmitted: 0,
      createdAt: new Date(),
      isAdmin: false,
      last_free_correction_at: '',
      has_used_pro_trial: false,
    };
  }

  private async ensureProfileExists(user: SupabaseUser): Promise<void> {
    const resolvedName =
      (user.user_metadata?.name as string | undefined)
      ?? user.email
      ?? 'Usuário';

    const { data: existingProfile, error: existingProfileError } = await this.supabase
      .from('profiles')
      .select('id, name, email')
      .eq('id', user.id)
      .maybeSingle();

    if (existingProfileError) {
      throw new Error(`Erro ao consultar perfil existente: ${existingProfileError.message}`);
    }

    if (existingProfile) {
      const patch: { name?: string; email?: string } = {};
      const existingName = String(existingProfile.name ?? '').trim();
      const existingEmail = String(existingProfile.email ?? '').trim();

      if (!existingName) {
        patch.name = resolvedName;
      }
      if (!existingEmail && user.email) {
        patch.email = user.email;
      }

      if (Object.keys(patch).length === 0) {
        return;
      }

      const { error: patchError } = await this.supabase
        .from('profiles')
        .update(patch)
        .eq('id', user.id);

      if (patchError) {
        throw new Error(`Erro ao atualizar dados básicos do perfil: ${patchError.message}`);
      }
      return;
    }

    const { error: insertError } = await this.supabase
      .from('profiles')
      .insert({
        id: user.id,
        email: user.email ?? '',
        name: resolvedName,
      });

    if (!insertError) {
      return;
    }

    const insertMessage = String(insertError.message ?? '').toLowerCase();
    const isDuplicate =
      insertError.code === '23505'
      || insertMessage.includes('duplicate')
      || insertMessage.includes('already exists');

    if (!isDuplicate) {
      throw new Error(`Erro ao garantir perfil: ${insertError.message}`);
    }
  }

  setPendingPriceId(priceId: string | null): void {
    this.pendingPriceId.set(priceId);
    if (isPlatformBrowser(this.platformId)) {
      if (priceId) {
        sessionStorage.setItem(PENDING_PRICE_ID_KEY, priceId);
      } else {
        sessionStorage.removeItem(PENDING_PRICE_ID_KEY);
      }
    }
  }

  async signUp(
    email: string,
    password: string,
    name: string,
    consent: { termsAccepted: boolean; privacyAccepted: boolean }
  ): Promise<void> {
    if (!email || !password || !name) {
      const message = 'Todos os campos são obrigatórios.';
      this.showNotification('error', 'Falha no cadastro', message);
      throw new Error(message);
    }

    if (!consent.termsAccepted || !consent.privacyAccepted) {
      const message = 'Você precisa aceitar os Termos de Serviço e a Política de Privacidade para continuar.';
      this.showNotification('error', 'Consentimento obrigatório', message);
      throw new Error(message);
    }

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          lgpd_consent_at: new Date().toISOString(),
          terms_version: LGPD_LEGAL_VERSION,
          privacy_policy_version: LGPD_LEGAL_VERSION,
        },
      },
    });

    if (error) {
      this.showNotification('error', 'Falha no cadastro', error.message);
      throw error;
    }

    if (data.user) {
      try {
        await this.ensureProfileExists(data.user);
      } catch {
      }

      await this.supabase
        .from('profiles')
        .update({
          lgpd_consent_at: new Date().toISOString(),
          terms_version: LGPD_LEGAL_VERSION,
          privacy_policy_version: LGPD_LEGAL_VERSION,
        } as any)
        .eq('id', data.user.id);
    }

    this.logService.log('INFO', 'Novo cadastro enviado');
    this.showNotification('success', 'Cadastro enviado', 'Confira seu e-mail para confirmar sua conta.');
  }

  async signIn(email: string, password: string, consentAccepted: boolean): Promise<void> {
    if (!consentAccepted) {
      const message = 'Você precisa aceitar os Termos de Serviço e a Política de Privacidade para continuar.';
      this.showNotification('error', 'Consentimento obrigatório', message);
      throw new Error(message);
    }

    if (!email || !password) {
      const message = 'Por favor, insira e-mail e senha.';
      this.showNotification('error', 'Falha no login', message);
      throw new Error(message);
    }

    const { data, error } = await this.withTimeout(
      this.supabase.auth.signInWithPassword({ email, password }),
      `Tempo limite no login após ${AUTH_OPERATION_TIMEOUT_MS}ms.`
    );
    if (error) {
      this.showNotification('error', 'Falha no login', error.message);
      throw error;
    }

    if (data?.session) {
      await this.setSession(data.session);
    }

    void this.recordLgpdConsentForCurrentUser().catch(() => undefined);
    this.showNotification('success', 'Login bem-sucedido', 'Bem-vindo(a)!');
  }

  setPendingLgpdConsent(consentAccepted: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (consentAccepted) {
      sessionStorage.setItem(PENDING_LGPD_CONSENT_KEY, '1');
    } else {
      sessionStorage.removeItem(PENDING_LGPD_CONSENT_KEY);
    }
  }

  private async applyPendingLgpdConsentIfAny(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const pendingConsent = sessionStorage.getItem(PENDING_LGPD_CONSENT_KEY);
    if (pendingConsent !== '1') {
      return;
    }

    const { data: currentSessionData } = await this.supabase.auth.getSession();
    if (!currentSessionData?.session?.access_token) {
      return;
    }

    const { data: currentUserData } = await this.supabase.auth.getUser();
    if (!currentUserData?.user) {
      return;
    }

    const nowIso = new Date().toISOString();
    const { error } = await this.supabase
      .from('profiles')
      .update({
        lgpd_consent_at: nowIso,
        terms_version: LGPD_LEGAL_VERSION,
        privacy_policy_version: LGPD_LEGAL_VERSION,
      } as any)
      .eq('id', currentUserData.user.id);

    sessionStorage.removeItem(PENDING_LGPD_CONSENT_KEY);
    if (error) {
      const lower = String(error.message ?? '').toLowerCase();
      const isAuthError = lower.includes('jwt') || lower.includes('unauthorized') || lower.includes('401');
      if (isAuthError) {
        return;
      }
      throw new Error(`Falha ao registrar consentimento LGPD: ${error.message}`);
    }
  }

  private async recordLgpdConsentForCurrentUser(): Promise<void> {
    const { data: currentSessionData } = await this.supabase.auth.getSession();
    if (!currentSessionData?.session?.access_token) {
      return;
    }

    const { data: currentUserData } = await this.supabase.auth.getUser();
    if (!currentUserData?.user) {
      return;
    }

    const { data: currentProfile } = await this.supabase
      .from('profiles')
      .select('lgpd_consent_at, terms_version, privacy_policy_version')
      .eq('id', currentUserData.user.id)
      .maybeSingle();

    const alreadyRegistered =
      Boolean((currentProfile as any)?.lgpd_consent_at)
      && String((currentProfile as any)?.terms_version ?? '') === LGPD_LEGAL_VERSION
      && String((currentProfile as any)?.privacy_policy_version ?? '') === LGPD_LEGAL_VERSION;

    if (alreadyRegistered) {
      return;
    }

    const nowIso = new Date().toISOString();
    await this.supabase
      .from('profiles')
      .update({
        lgpd_consent_at: nowIso,
        terms_version: LGPD_LEGAL_VERSION,
        privacy_policy_version: LGPD_LEGAL_VERSION,
      } as any)
      .eq('id', currentUserData.user.id);
  }

  private normalizeOAuthErrorMessage(rawError: string | null, rawDescription: string | null): string {
    const source = decodeURIComponent(rawDescription ?? rawError ?? '').toLowerCase();
    if (!source) {
      return 'Não foi possível concluir login com Google.';
    }

    if (source.includes('exchange external code')) {
      return 'Não foi possível concluir login com Google. Tente novamente em alguns segundos.';
    }

    if (source.includes('invalid') || source.includes('expired') || source.includes('grant')) {
      return 'Código de login expirado. Tente entrar novamente com Google.';
    }

    if (source.includes('redirect') || source.includes('callback')) {
      return 'Falha de configuração de callback do Google. Contate o suporte.';
    }

    return 'Não foi possível concluir login com Google.';
  }

  private cleanupOAuthParamsFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const url = new URL(window.location.href);
    const oauthParams = [
      'code',
      'state',
      'error',
      'error_description',
      'scope',
      'authuser',
      'prompt',
    ];

    for (const param of oauthParams) {
      url.searchParams.delete(param);
    }

    const search = url.searchParams.toString();
    const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }

  private async waitForOAuthSession(timeoutMs = 3500): Promise<Session | null> {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const { data } = await this.supabase.auth.getSession();
      if (data.session) {
        return data.session;
      }
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    return null;
  }

  async finalizeOAuthRedirectIfNeeded(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const url = new URL(window.location.href);
    const hasOauthCode = url.searchParams.has('code');
    const oauthError = url.searchParams.get('error');
    const oauthErrorDescription = url.searchParams.get('error_description');

    if (!hasOauthCode && !oauthError) {
      return;
    }

    if (oauthError) {
      const safeMessage = this.normalizeOAuthErrorMessage(oauthError, oauthErrorDescription);
      this.showNotification('error', 'Falha no login com Google', safeMessage);
      this.cleanupOAuthParamsFromUrl();
      return;
    }

    const session = await this.waitForOAuthSession();
    if (session) {
      await this.setSession(session);
      await this.applyPendingLgpdConsentIfAny().catch(() => undefined);
      this.cleanupOAuthParamsFromUrl();
      return;
    }

    this.cleanupOAuthParamsFromUrl();
    throw new Error('Não foi possível concluir login com Google. Tente novamente.');
  }

  private async getValidAccessToken(): Promise<string> {
    const sessionToken = this.session()?.access_token?.trim();
    if (sessionToken) {
      return sessionToken;
    }

    const { data, error } = await this.supabase.auth.getSession();
    if (error) {
      throw new Error(`Falha ao validar sessão: ${error.message}`);
    }

    const token = data.session?.access_token?.trim();
    if (!token) {
      throw new Error('Você precisa estar logado para continuar.');
    }

    return token;
  }

  async deleteMyAccount(): Promise<void> {
    const token = await this.getValidAccessToken();

    const response = await fetch(`${this.supabaseService.supabaseUrl}/functions/v1/privacy-delete-account`, {
      method: 'POST',
      headers: {
        apikey: this.supabaseService.supabaseKey,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message = payload?.error || payload?.message || 'Falha ao excluir conta.';
      throw new Error(message);
    }

    await this.signOut();
  }

  async exportMyData(): Promise<void> {
    const token = await this.getValidAccessToken();

    const response = await fetch(`${this.supabaseService.supabaseUrl}/functions/v1/privacy-export`, {
      method: 'POST',
      headers: {
        apikey: this.supabaseService.supabaseKey,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      let payload: any = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }
      const message = payload?.error || payload?.message || 'Falha ao exportar dados.';
      throw new Error(message);
    }

    const data = await response.json();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `correctify-meus-dados-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async requestPasswordReset(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error('Informe um e-mail válido para continuar.');
    }

    const redirectTo = isPlatformBrowser(this.platformId)
      ? `${window.location.origin}/`
      : undefined;

    const { error } = await this.supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo,
    });

    if (error) {
      throw new Error(error.message);
    }

    this.logService.log('INFO', 'Solicitacao de redefinicao de senha enviada');
  }

  async signOut(): Promise<void> {
    this.setPendingLgpdConsent(false);
    this.clearSession();
    this.clearPersistedAuthStorage();

    void this.supabase.auth.signOut({ scope: 'local' }).catch((error) => {
      void error;
    });
  }

  async decrementCredit(): Promise<void> {
    const user = this.currentUser();
    if (!user || (user.credits <= 0 && user.credits < 900)) {
      throw new Error('Nenhum usuário ou créditos insuficientes.');
    }
    if (user.credits > 900) return;

    const nextCredits = user.credits - 1;
    const { error } = await this.supabase
      .from('profiles')
      .update({ credits: nextCredits })
      .eq('id', user.id);

    if (error) throw new Error(`Falha ao atualizar créditos: ${error.message}`);
    this.currentUser.update((u) => (u ? { ...u, credits: nextCredits } : null));
  }

  async updateLastFreeCorrectionDate(): Promise<void> {
    await this.consumeFreeCredit({ useProTrial: false });
  }

  async markProTrialAsUsed(): Promise<void> {
    const user = this.currentUser();
    if (!user) throw new Error('Usuário não encontrado.');

    const { error } = await this.supabase
      .from('profiles')
      .update({ has_used_pro_trial: true })
      .eq('id', user.id);

    if (error) throw new Error(`Falha ao marcar trial: ${error.message}`);
    this.currentUser.update((u) => {
      if (!u) return null;
      return { ...u, has_used_pro_trial: true };
    });
  }

  private async loadUserProfile(): Promise<void> {
    const session = this.session();
    if (!session?.user) {
      this.clearSession();
      return;
    }

    const { data: authData, error: authError } = await this.supabase.auth.getUser(session.access_token);
    if (authError || !authData?.user) {
      throw new Error(authError?.message || 'Invalid JWT');
    }
    const authUser = authData.user;

    let { data, error } = await this.supabase
      .from('profiles')
      .select('id, name, email, credits, plan, "isAdmin", last_free_correction_at, has_used_pro_trial')
      .eq('id', authUser.id)
      .maybeSingle();

    if (!data) {
      await this.ensureProfileExists(authUser);
      const retried = await this.supabase
        .from('profiles')
        .select('id, name, email, credits, plan, "isAdmin", last_free_correction_at, has_used_pro_trial')
        .eq('id', authUser.id)
        .maybeSingle();
      data = retried.data;
      error = retried.error;
    }

    if (error || !data) {
      throw new Error(`Erro ao carregar perfil: ${error?.message ?? 'Perfil não encontrado'}`);
    }

    const appUser: AppUser = {
      id: data.id,
      name: data.name ?? authUser.email ?? 'Usuário',
      email: data.email ?? authUser.email ?? '',
      credits: Math.max(0, Number(data.credits ?? 0)),
      plan: (data.plan ?? 'gratuito') as UserPlan,
      essaysSubmitted: 0,
      createdAt: new Date(),
      isAdmin: Boolean((data as any).isAdmin ?? (data as any).isadmin),
      last_free_correction_at: data.last_free_correction_at ?? '',
      has_used_pro_trial: data.has_used_pro_trial ?? false,
    };

    this.currentUser.set(appUser);
    this.isAdmin.set(Boolean((data as any).isAdmin ?? (data as any).isadmin));
    this.isAuthenticated.set(true);
  }

  async refreshUserProfile(): Promise<void> {
    if (this.profileLoadInFlight) {
      return this.profileLoadInFlight;
    }

    const timedLoad = (async () => {
      try {
        await this.withTimeout(
          this.loadUserProfile(),
          `Tempo limite ao carregar perfil após ${PROFILE_LOAD_TIMEOUT_MS}ms.`,
          PROFILE_LOAD_TIMEOUT_MS
        );
      } catch (error: any) {
        const message = String(error?.message ?? '').toLowerCase();
        const isAuthError =
          message.includes('jwt')
          || message.includes('token')
          || message.includes('unauthorized')
          || message.includes('401');

        if (!isAuthError) {
          throw error;
        }

        const { data, error: refreshError } = await this.supabase.auth.refreshSession();
        if (refreshError || !data.session) {
          this.handleSessionExpired();
          throw new Error('Sessão expirada. Faça login novamente.');
        }

        this.session.set(data.session);
        try {
          await this.withTimeout(
            this.loadUserProfile(),
          `Tempo limite ao carregar perfil após ${PROFILE_LOAD_TIMEOUT_MS}ms.`,
            PROFILE_LOAD_TIMEOUT_MS
          );
        } catch (secondError: any) {
          const secondMessage = String(secondError?.message ?? '').toLowerCase();
          const isStillAuthError =
            secondMessage.includes('jwt')
            || secondMessage.includes('token')
            || secondMessage.includes('unauthorized')
            || secondMessage.includes('401');

          if (isStillAuthError) {
            this.handleSessionExpired();
            throw new Error('Sessão expirada. Faça login novamente.');
          }

          throw secondError;
        }
      }
    })();

    this.profileLoadInFlight = timedLoad.finally(() => {
      this.profileLoadInFlight = null;
    });

    return this.profileLoadInFlight;
  }
}
